import { useNavigate, useLocation } from "react-router-dom";
import { Campaign, UserService } from "../../services/UserService";
import React, { useState, useEffect, useRef } from "react";
import { AuthService } from "../../services/AuthService";
import { LayoutDashboard, BarChart3, Bell } from "lucide-react";
import hubLogo from "../../assets/branding/logo_white.png";
import "./style.scss";

const extractFirstName = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.split(" ")[0];
};

interface HeaderUserProps {
  campaigns: Campaign[] | null;
  selectedCampaign: Campaign | null;
  onSelectCampaign: (campaign: Campaign) => void;
  onFocusMode?: (value: boolean) => void;
}

const HeaderUser: React.FC<HeaderUserProps> = ({ campaigns, selectedCampaign, onSelectCampaign, onFocusMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(
    AuthService.getFullName() || AuthService.getUsername()
  );
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false);
  const campaignMenuRef = useRef<HTMLDivElement | null>(null);
  const isAdmin = AuthService.isAdmin();
  const userRole = AuthService.getUserRole();

  useEffect(() => {
    if (!displayName) {
      UserService.getUserWithCamaigns()
        .then((user) => {
          const name = user.full_name || user.username;
          setDisplayName(name);
          if (user.full_name) localStorage.setItem("fullName", user.full_name);
          localStorage.setItem("username", user.username);
        })
        .catch(() => null);
    }
  }, [displayName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (campaignMenuRef.current && !campaignMenuRef.current.contains(event.target as Node)) {
        setCampaignMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const firstName = extractFirstName(displayName);
  const nameLabel = firstName || displayName || "Perfil";
  const initial = nameLabel.charAt(0).toUpperCase();

  const navItems = [
    { label: "Dashboard", path: "/", icon: LayoutDashboard },
    { label: "Notificações", path: "/notificacoes", icon: Bell },
    ...(userRole === "USER_CLIENT_ADMIN" ? [{ label: "Equipe", path: "/equipe", icon: BarChart3 }] : []),
  ];

  const isPathActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const isNotificationsPage = location.pathname.startsWith("/notificacoes");
  const showFocusButton = location.pathname === "/" && !isNotificationsPage;
  const handleToggleFocus = () => {
    const next = !isHeaderVisible;
    setIsHeaderVisible(next);
    onFocusMode?.(!next);
  };

  return (
    <>
      {isHeaderVisible && (
        <header
          className="header-user sticky top-0 z-[100] w-full"
          style={{
            background: "linear-gradient(140deg, #000208 0%, #01050f 40%, #01060f 100%)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
            borderBottom: "1px solid rgba(10, 20, 40, 0.7)",
          }}
        >
          {/* Desktop */}
          <div className="hidden md:flex header-desktop px-5">
            {(userRole === "ADMIN" || userRole === "USER_HUB") ? (
              <div
                role="button"
                tabIndex={0}
                className="brand"
                onClick={() => navigate("/")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate("/");
                  }
                }}
              >
                <img src={hubLogo} alt="Hub Brasil" className="hub-logo" />
              </div>
            ) : (
              <div className="brand-placeholder" />
            )}

            <ul className="menu">
              {navItems.map(({ label, path, icon }) => {
                const active = isPathActive(path);
                const Icon = icon;
                return (
                  <li
                    key={path}
                    role="button"
                    tabIndex={0}
                    aria-current={active ? "page" : undefined}
                    className={`menu-item ${active ? "active" : ""}`}
                    onClick={() => navigate(path)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(path);
                      }
                    }}
                  >
                    <span className="menu-icon" aria-hidden="true">
                      <Icon size={16} />
                    </span>
                    <span className="menu-label">{label}</span>
                  </li>
                );
              })}
            </ul>

            {showFocusButton && (
              <div className="focus-button-wrapper">
                <button type="button" className="focus-button" onClick={handleToggleFocus}>
                  Focar dashboard
                </button>
              </div>
            )}

            <div className="campaign-actions" ref={campaignMenuRef}>
              <div className="campaign-dropdown">
                <button
                  type="button"
                  className="campaign-trigger"
                  onClick={() => setCampaignMenuOpen((prev) => !prev)}
                >
                  <span className="trigger-label">
                    {selectedCampaign?.name || "Selecionar"}
                  </span>
                  <span className="trigger-chevron">{campaignMenuOpen ? "▴" : "▾"}</span>
                </button>
                {campaignMenuOpen && (
                  <div className="campaign-menu">
                    {campaigns && campaigns.length > 0 ? (
                      campaigns.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={`campaign-item ${c.id === selectedCampaign?.id ? "active" : ""}`}
                          onClick={() => {
                            onSelectCampaign(c);
                            setCampaignMenuOpen(false);
                          }}
                        >
                          {c.name}
                        </button>
                      ))
                    ) : (
                      <div className="campaign-empty">Sem campanhas disponíveis</div>
                    )}
                  </div>
                )}
              </div>

              <div className="user-profile">
                <button
                  type="button"
                  className="user-chip"
                  onClick={() => setProfileMenuOpen((prev) => !prev)}
                >
                  <span className="name">{nameLabel}</span>
                  <span className="avatar">{initial}</span>
                </button>
                {profileMenuOpen && (
                  <div className="profile-menu">
                    {[
                      { label: "Meu perfil", path: "/perfil" },
                      { label: "Notificações", path: "/notificacoes" },
                      { label: "Relatórios", path: "/relatorios" },
                      ...(isAdmin ? [{ label: "Painel do admin", path: "/admin" }] : []),
                    ].map(({ label, path }) => (
                      <button
                        key={path}
                        type="button"
                        className="profile-item"
                        onClick={() => navigate(path)}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="profile-item logout"
                      onClick={() => AuthService.logout()}
                    >
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="flex flex-col md:hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <div role="button" tabIndex={0} className="cursor-pointer" onClick={() => navigate("/")}>
                <img src={hubLogo} alt="Hub Brasil" className="h-7 w-auto block" />
              </div>
              <select
                id="campaign-select-mobile"
                value={selectedCampaign?.id || ""}
                onChange={e => {
                  const id = Number(e.target.value);
                  const c = campaigns?.find(c => c.id === id);
                  if (c) onSelectCampaign(c);
                }}
                className="px-2 py-1.5 rounded-lg border text-sm font-medium mx-2 flex-1 outline-none"
                style={{
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.12)"
                }}>
                {campaigns && campaigns.length > 0
                  ? campaigns.map(c => <option key={c.id} value={c.id} style={{ color: "#0b1a30" }}>{c.name}</option>)
                  : <option disabled>Sem campanhas disponíveis</option>}
              </select>
              <button className="w-8 h-8 flex items-center justify-center rounded-full text-lg cursor-pointer border-none bg-transparent text-white"
                onClick={() => setIsMenuOpen(prev => !prev)}>☰</button>
            </div>
            {isMenuOpen && (
              <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.1)", background: "#112240" }}>
                {[
                  { label: "Dashboard", path: "/" }, { label: "Relatórios", path: "/relatorios" },
                  { label: "Notificações", path: "/notificacoes" },
                  ...(isAdmin ? [{ label: "Painel do admin", path: "/admin" }] : []),
                  { label: "Meu perfil", path: "/perfil" },
                ].map(({ label, path }) => (
                  <button key={path} className="w-full text-left px-5 py-3.5 text-sm font-semibold cursor-pointer border-none bg-transparent transition-all duration-150 text-white hover:bg-white/10"
                    onClick={() => { setIsMenuOpen(false); navigate(path); }}>{label}</button>
                ))}
                <button className="w-full text-left px-5 py-3.5 text-sm font-semibold cursor-pointer border-none bg-transparent text-red-400 hover:text-red-300 hover:bg-white/10 transition-colors"
                  onClick={() => AuthService.logout()}>Sair</button>
              </div>
            )}
          </div>
        </header>
      )}

      {!isHeaderVisible && (
        <button
          aria-label="Desfocar dashboard"
          onClick={handleToggleFocus}
          className="fixed top-4 right-4 z-[200] w-11 h-11 rounded-full flex items-center justify-center text-xl font-bold cursor-pointer bg-white/20 hover:bg-white/35 transition-all backdrop-blur-sm"
          style={{ border: "1px solid rgba(255,255,255,0.2)", color: "#000", boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
          ×
        </button>
      )}
    </>
  );
};

export default HeaderUser;
